import type { DdjjPreguntaPropia } from './services/empresas.service';
import type { ViewKey } from './permissions/catalog';
import type { EquipoVenta } from './catalogs/equipos-venta';

export type EstadoAsociado = 'ok' | 'error';
export type EstadoPadron = 'borrador' | 'pendiente_validacion' | 'cerrado';

export enum UserRole {
  SUPERADMIN_MEDICUS = 'SUPERADMIN_MEDICUS',
  ADMIN_MEDICUS = 'ADMIN_MEDICUS',
  /** Medicus de solo lectura: ve todo, no ejecuta mutaciones. */
  USER_MEDICUS = 'USER_MEDICUS',
  USUARIO_EMPRESA = 'USUARIO_EMPRESA',
  /** Telemarketer: buscador + su CRM de contactos (worklist + cartera). */
  TELEMARKETER = 'TELEMARKETER',
  /**
   * Telemarketer INTERNO de Medicus (Keycloak `user-tmk-medicus`). Mismas
   * vistas y mismos permisos que TELEMARKETER; existe para separar al personal
   * de Medicus de los agentes externos. Usar `esTelemarketer()` para gatear,
   * no comparar contra TELEMARKETER suelto.
   */
  TELEMARKETER_MEDICUS = 'TELEMARKETER_MEDICUS',
  /** Agente externo de SkyTel: CRM TMK scopeado a la cohorte cedida. */
  SKYTEL = 'SKYTEL',
  /** Integración de un tercero por API (m2m). No usa la web. */
  INTEGRACION = 'INTEGRACION',
}

export interface User {
  id: string;
  email: string;
  nombre: string;
  rol: UserRole;
  empresaIds: string[];
  activo: boolean;
  pendienteActivacion: boolean;
  createdAt: string;
  /** Permiso efectivo a nivel vista (lo manda el backend en /login y /me). */
  permisos?: ViewKey[];
  /** Overrides crudos de permisos por vista (para el editor de Usuarios). */
  overrides?: { allow: string[]; deny: string[] };
  /**
   * Usuario de Binary del promotor (`SA_PROMOTORES_EMPLEADORES.USR_USERNAME`).
   * Con él se resuelve de qué empresas puede cargar padrones y qué promotor va
   * a las altas. Vacío = no tiene perfil de promotor.
   */
  binaryUsername?: string;
  /** Equipo de ventas (VEN-516). `null` = sin equipo. */
  equipo?: EquipoVenta | null;
}

export interface UsuarioListItem extends User {
  invitedAt?: string;
  activationUrl?: string;
}

export interface AltaPlan {
  /** PLAN_CODIGO Oracle. */
  codigo: string;
  /** Nombre legible del plan. */
  nombre?: string;
  /** SUC_EMP_CODIGO de este plan. */
  sucursalId?: string;
  /** CATAFI_CODIGO de este plan. */
  categoriaAfiliadoCodigo?: string;
  /** AA_CODIGO de este plan. */
  aaCodigo?: string;
  /** AA2_CODIGO de este plan. */
  aa2Codigo?: string;
  /** Plan base (default del alta). */
  esBase?: boolean;
  /** Disponible para la venta: solo estos se le ofrecen al promotor en un padrón. */
  disponibleVenta?: boolean;
}

export interface AltaConfig {
  /** Habilita el alta por API (Binary) para esta empresa. */
  habilitada?: boolean;
  /** OSOC_CODIGO (obra social) → campo obraSocial del alta. Compartido. */
  obraSocialCodigo?: string;
  /** Usuario promotor en Binary. Compartido. */
  promotorId?: string;
  /** Usuario de firma de la solicitud. Compartido. */
  firmaSolicitud?: string;
  /** Agencia de envío (default '1'). Compartido. */
  agenciaEnvio?: string;
  /** Planes (base + opciones) con sus códigos por plan. */
  planes?: AltaPlan[];
  /** @deprecated usar planes[].sucursalId */
  sucursalId?: string;
  /** @deprecated usar planes[].categoriaAfiliadoCodigo */
  categoriaAfiliadoCodigo?: string;
  /** @deprecated usar planes[].aaCodigo */
  aaCodigo?: string;
  /** @deprecated usar planes[].aa2Codigo */
  aa2Codigo?: string;
}

export interface Empresa {
  id: string;
  cuit: string;
  razonSocial: string;
  nombreComercial: string;
  contactoNombre: string;
  contactoEmail: string;
  contactoTelefono?: string;
  activa: boolean;
  oracleEmplId?: number;
  planBaseCodigo?: string;
  planSuperadorCodigo?: string;
  altaConfig?: AltaConfig;
  /**
   * Config de la DDJJ: qué preguntas del catálogo NO se le hacen a esta
   * empresa; o, si tiene cuestionario propio (`preguntas`), ESE cuestionario en
   * lugar del catálogo (las excluidas no aplican).
   */
  ddjjConfig?: {
    preguntasExcluidas?: string[];
    preguntas?: DdjjPreguntaPropia[];
    origen?: 'catalogo' | 'binary' | 'excel';
    preguntasActualizadasAt?: string;
  };
  /**
   * Sin asistente del titular: al validar un padrón, sus legajos no reciben la
   * invitación al onboarding y pasan directo a "listo para alta" (los bis
   * quedan activados). Ausente/false = con asistente (lo que hace MS).
   */
  sinWizard?: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
}

export interface ValidationError {
  field?: string;
  rule: string;
  message: string;
  /** Info adicional para errores accionables (ej. unique.cross-padron). */
  meta?: Record<string, string | number | boolean>;
}

export interface Asociado {
  id: string;
  padronId: string;
  rowNumber: number;
  estado: EstadoAsociado;
  errores: ValidationError[];
  legajo?: string;
  parentesco?: string;
  apellSolt?: string;
  nombres?: string;
  fechaNacimiento?: string;
  nacionalidad?: string;
  sexo?: string;
  estadoCivil?: string;
  tipoDoc?: string;
  documento?: string;
  cuil?: string;
  domicilio?: string;
  numero?: string;
  piso?: string;
  depto?: string;
  cp?: string;
  areaCelular?: string;
  numeroCelular?: string;
  email?: string;
  renaperValidacion?: {
    estado: 'pendiente' | 'validado' | 'no_coincide' | 'no_encontrado' | 'error';
    consultadoAt: string;
    motivo?: string;
    detalle?: {
      matchApellido?: boolean;
      matchNombre?: boolean;
      matchFecha?: boolean;
      codigo?: number;
    };
    proveedor?: string;
    /**
     * Lista de campos que se pisaron sobre el asociado con valores oficiales
     * de Renaper cuando el estado fue `no_coincide`. Solo informativo — el
     * pisaje ya se aplicó al asociado.
     */
    pisos?: Array<{
      campo: 'apellSolt' | 'nombres' | 'fechaNacimiento';
      valorAnterior: string;
      valorNuevo: string;
    }>;
  };
}

export interface PadronSummary {
  id: string;
  empresaId: string;
  subidoPor: string;
  nombreArchivo: string;
  fechaSubida: string;
  totalRegistros: number;
  registrosOk: number;
  registrosError: number;
  /** Asociados en estado ok pero con advertencia (ej. familiar sin titular cuyo
   * número coincide con un legajo ya creado → se agrega desde Legajos bis). */
  registrosAdvertencia?: number;
  estado: EstadoPadron;
  /**
   * Plan con el que se cargó el padrón (congelado al subir) y promotor de
   * Binary de quien lo cargó. Los padrones anteriores a la feature no los
   * tienen: entraron con el plan base y el promotor de la organización.
   */
  planCodigo?: string;
  /**
   * Sucursal del empleador en la que entra el padrón (va con `planCodigo`: el
   * mismo plan puede estar en varias sucursales). Vacía en padrones viejos.
   */
  sucursalId?: string;
  promotorUsername?: string;
}

export type Padron = PadronSummary;

export interface InconsistenciaAgrupada {
  rule: string;
  field?: string;
  cantidad: number;
  ejemplos: string[];
}

export interface InconsistenciasResponse {
  total: number;
  resumen: InconsistenciaAgrupada[];
}

export interface AsociadosPage {
  total: number;
  offset: number;
  limit: number;
  items: Asociado[];
}

export const COLUMNAS_VISIBLES: Array<{ key: keyof Asociado; header: string }> = [
  { key: 'rowNumber', header: '#' },
  { key: 'legajo', header: 'Legajo' },
  { key: 'parentesco', header: 'Parent.' },
  { key: 'apellSolt', header: 'Apellido' },
  { key: 'nombres', header: 'Nombres' },
  { key: 'cuil', header: 'CUIL' },
  { key: 'documento', header: 'Documento' },
  { key: 'fechaNacimiento', header: 'F. Nac.' },
  { key: 'email', header: 'E-mail' },
];

export const ROL_LABELS: Record<UserRole, string> = {
  [UserRole.SUPERADMIN_MEDICUS]: 'Super Admin Medicus',
  [UserRole.ADMIN_MEDICUS]: 'Admin Medicus',
  [UserRole.USER_MEDICUS]: 'Usuario Medicus (solo lectura)',
  [UserRole.USUARIO_EMPRESA]: 'Usuario de Organización',
  [UserRole.TELEMARKETER]: 'Telemarketer',
  [UserRole.TELEMARKETER_MEDICUS]: 'Telemarketer Medicus',
  [UserRole.SKYTEL]: 'Agente SkyTel',
  [UserRole.INTEGRACION]: 'Integración (API)',
};

/** true para cualquier telemarketer (externo o interno de Medicus). */
export function esTelemarketer(rol: UserRole): boolean {
  return rol === UserRole.TELEMARKETER || rol === UserRole.TELEMARKETER_MEDICUS;
}

/** true para cualquier rol del equipo Medicus (incluye solo lectura). */
export function isMedicus(rol: UserRole): boolean {
  return (
    rol === UserRole.SUPERADMIN_MEDICUS ||
    rol === UserRole.ADMIN_MEDICUS ||
    rol === UserRole.USER_MEDICUS
  );
}

/**
 * `false` para el rol Medicus de solo lectura. La UI usa este flag para
 * ocultar botones que disparan mutaciones. NO reemplaza la autorización
 * del server — solo evita mostrar acciones que el server rechazaría.
 */
export function canMutate(rol: UserRole): boolean {
  return rol !== UserRole.USER_MEDICUS;
}
